import { lazy, type ComponentType } from 'react';
import { logger } from './logger';

// Recovery for stale code-split chunks after a deploy.
//
// Every lazy page is emitted as a content-hashed file (e.g. ModuleViewer-BZlb3zeA.js).
// A tab opened before a deploy still holds the OLD entry bundle, so the first
// visit to a not-yet-loaded page requests an old hash that the new container no
// longer serves → 404 → "Failed to fetch dynamically imported module". A full
// reload fetches the fresh index.html (served no-cache by nginx) and fixes it.
//
// Guard: at most one automatic reload per RELOAD_WINDOW_MS, so a genuinely
// broken build shows the friendly error screen instead of a reload loop.

const RELOAD_KEY = 'udb_chunk_reload_at';
const RELOAD_WINDOW_MS = 20_000;

let reloadInProgress = false;

// Chrome / Firefox / Safari word this error differently; Vite adds its own for CSS.
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module', // Chrome, Edge
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari
  'unable to preload css', // Vite CSS preload
  'loading chunk', // legacy webpack-style wording
  'loading css chunk',
];

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function readLastReload(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    return 0;
  }
}

/**
 * Reloads the page to pick up the latest build. Returns true when a reload is
 * (already) under way, false when the guard blocked it — the caller should then
 * surface the error instead.
 */
export function reloadForChunkError(error?: unknown): boolean {
  if (reloadInProgress) return true;
  // Offline: a reload would only land on the browser's offline page.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  if (Date.now() - readLastReload() < RELOAD_WINDOW_MS) return false;

  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Without sessionStorage the loop guard can't work — don't auto-reload.
    return false;
  }
  reloadInProgress = true;
  logger.warn('[chunk-reload] stale chunk after deploy, reloading', {
    message: error instanceof Error ? error.message : undefined,
  });
  window.location.reload();
  return true;
}

/** Manual "Reload page" button — bypasses the auto-reload guard. */
export function forceReload(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // ignore
  }
  window.location.reload();
}

/**
 * Drop-in replacement for React.lazy. On a stale-chunk failure it reloads the
 * page and keeps the Suspense fallback on screen until the reload happens, so
 * the user never sees an error — they land on the page they clicked.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own signature
export function lazyWithReload<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(() =>
    factory().catch((error: unknown) => {
      if (isChunkLoadError(error) && reloadForChunkError(error)) {
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }),
  );
}
